// New client / edit client. A long onboarding form, so: a section index on wide screens, errors
// shown under the field they belong to, a save bar that stays in view with an "Unsaved changes"
// state, and a guard against leaving with unsaved work.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, Camera, Check, ChevronLeft, FileUp, Loader2, MapPin, Sparkles, Trash2, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { resizeImageFile } from '../../utils/imageResize';
import {
  ErrorState, Field, FormRow, PAGE, PageHeader, PersonAvatar, Switch, plural,
} from '../../components/ui';
import { clientPayload, dayOf, fullName, todayIso, useUnsavedGuard } from './clientShared';
import { ClientTypeSelect, GenderSelect, INVOICE_DELIVERY, STATES } from './clientFields';

const EMPTY = () => ({
  firstName: '', lastName: '', dateOfBirth: '', gender: '', sex: '',
  ndisNumber: '', agedCareRecipientId: '', clientIdExternal: '',
  referenceNumber: '', poNumber: '', customField: '',
  clientType: 'ndis_managed', defaultPriceBookId: '',
  shareProgressNotes: false, generalInformation: '', privateNotes: '',
  address: '', suburb: '', state: '', postcode: '', latitude: null, longitude: null,
  nextReviewDate: '', joinedDate: todayIso(),
  invoiceDeliveryMethod: 'email',
  planPeriodStart: '', planPeriodEnd: '',
  riskAlert: '', riskNotification: '', allergies: '',
  countryOfBirth: '', religion: '', indigenousStatus: '', caldBackground: '',
  interpreterRequired: false, preferredCarerGender: '', languagesSpoken: '',
  aiProcessingConsent: true, // AI consent is auto-allowed org-wide for new clients
});

const SECTIONS = [
  { id: 'basic', label: 'Basic information', required: ['firstName', 'lastName', 'dateOfBirth'] },
  { id: 'ids', label: 'Identification' },
  { id: 'billing', label: 'Billing and settings' },
  { id: 'address', label: 'Address' },
  { id: 'safety', label: 'Safety and medical' },
  { id: 'culture', label: 'Culture and preferences' },
  { id: 'general', label: 'General information' },
  { id: 'private', label: 'Private notes' },
];
const SECTION_OF = { firstName: 'basic', lastName: 'basic', dateOfBirth: 'basic', ndisNumber: 'ids', postcode: 'address', planPeriodEnd: 'billing' };
const FIELD_ORDER = ['firstName', 'lastName', 'dateOfBirth', 'ndisNumber', 'planPeriodEnd', 'postcode'];

const FIELD_LABELS = {
  firstName: 'first name', lastName: 'last name', dateOfBirth: 'date of birth', gender: 'gender', sex: 'sex',
  ndisNumber: 'NDIS number', agedCareRecipientId: 'aged care recipient ID', referenceNumber: 'reference number',
  clientType: 'plan management', address: 'street address', suburb: 'suburb', state: 'state', postcode: 'postcode',
  generalInformation: 'general information', nextReviewDate: 'next review',
};

const fid = (k) => `cf-${k}`;

// Format rules are checked on fields someone changed, so an older record with a legacy value can
// still be saved after editing something else.
function validate(f, initial) {
  const e = {};
  if (!f.firstName.trim()) e.firstName = 'Enter a first name';
  if (!f.lastName.trim()) e.lastName = 'Enter a last name';
  if (!f.dateOfBirth) e.dateOfBirth = 'Enter a date of birth';
  else if (f.dateOfBirth > todayIso()) e.dateOfBirth = 'The date of birth can’t be in the future';
  const ndis = String(f.ndisNumber || '').replace(/\s+/g, '');
  if (ndis && ndis !== String(initial.ndisNumber || '').replace(/\s+/g, '') && !/^4\d{8}$/.test(ndis)) {
    e.ndisNumber = 'Enter the 9-digit NDIS number (starts with 43)';
  }
  const pc = String(f.postcode || '').trim();
  if (pc && pc !== String(initial.postcode || '').trim() && !/^\d{4}$/.test(pc)) e.postcode = 'Enter a 4-digit postcode';
  if (f.planPeriodStart && f.planPeriodEnd && f.planPeriodEnd < f.planPeriodStart) e.planPeriodEnd = 'The plan end is before its start';
  return e;
}

// The API answers some mistakes in words; put those under the field they are about.
function fieldFromServer(msg) {
  const m = String(msg || '').toLowerCase();
  if (m.includes('first name') && m.includes('last name')) return { firstName: 'Enter a first name', lastName: 'Enter a last name' };
  if (m.includes('first name')) return { firstName: msg };
  if (m.includes('last name')) return { lastName: msg };
  if (m.includes('date of birth')) return { dateOfBirth: msg };
  if (m.includes('ndis')) return { ndisNumber: msg };
  return null;
}

const comparable = (f) => JSON.stringify({ ...f, _ai: undefined });

function AiTag() {
  return (
    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-slate-100 px-1 text-[10.5px] font-semibold text-slate-600 align-middle" title="Filled from the uploaded document">
      <Sparkles size={10} className="text-violet-500" aria-hidden />AI
    </span>
  );
}

function FormSection({ id, title, hint, children }) {
  return (
    <section id={`sec-${id}`} aria-labelledby={`sec-${id}-title`} className="scroll-mt-24 bg-white border border-slate-200 rounded-xl">
      <div className="px-5 py-3.5 border-b border-slate-100">
        <h2 id={`sec-${id}-title`} className="text-sm font-semibold text-slate-800">{title}</h2>
        {hint && <p className="text-[12.5px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </section>
  );
}

// Profile photo. Editing: saved straight away (as before). New client: held until the client
// exists, then uploaded.
function PhotoPicker({ clientId, photo, firstName, lastName, pending, onPending, onSaved }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = pending ? pending.url : photo;

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choose an image file (PNG, JPG or WebP)'); return; }
    setBusy(true);
    try {
      const blob = await resizeImageFile(file, 400, 0.85);
      if (!clientId) { onPending({ blob, url: URL.createObjectURL(blob) }); return; }
      const fd = new FormData();
      fd.append('photo', new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
      const { data } = await api.post(`/clients/${clientId}/photo`, fd);
      onSaved(data.profile_photo_url || null);
      toast.success('Photo updated');
    } catch (err) { toast.error(err.response?.data?.message || 'The photo didn’t upload'); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!clientId) { onPending(null); return; }
    setBusy(true);
    try { await api.delete(`/clients/${clientId}/photo`); onSaved(null); toast.success('Photo removed'); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not remove the photo'); }
    finally { setBusy(false); }
  }

  return (
    <div className="flex items-center gap-4">
      <PersonAvatar id={clientId || 'new'} photo={preview} firstName={firstName} lastName={lastName} size="xl" />
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="btn-secondary">
          {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Camera size={14} aria-hidden />}
          {preview ? 'Change photo' : 'Add photo'}
        </button>
        {preview && (
          <button type="button" onClick={remove} disabled={busy} className="btn-ghost text-slate-600">
            <Trash2 size={14} aria-hidden /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function ClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [record, setRecord] = useState(null);
  const [loadState, setLoadState] = useState({ loading: isEdit, error: null });
  const [saving, setSaving] = useState(false);
  const [priceBooks, setPriceBooks] = useState([]);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiFilled, setAiFilled] = useState(() => new Set());
  const [aiExtras, setAiExtras] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [activeSection, setActiveSection] = useState('basic');
  const aiFileRef = useRef(null);

  useEffect(() => {
    api.get('/price-books').then((r) => setPriceBooks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!isEdit) return;
    setLoadState({ loading: true, error: null });
    api.get(`/clients/${id}`)
      .then((r) => {
        const c = r.data;
        // The whole record goes back on save (the API replaces it), including the parts this form
        // doesn't show: the need-to-know lists and AI consent keep exactly their stored values.
        const f = { ...clientPayload(c), joinedDate: dayOf(c.joined_date) };
        setRecord(c);
        setForm(f);
        setInitial(f);
        setPhotoUrl(c.profile_photo_url || null);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => setLoadState({ loading: false, error: e.response?.status === 404 ? 'This client was not found.' : (e.response?.data?.message || 'We couldn’t load this client. Check your connection, then try again.') }));
  }, [id, isEdit]);
  useEffect(() => { load(); }, [load]);

  // Revoke the preview URL of a held photo once it is replaced or the page closes.
  useEffect(() => () => { if (pendingPhoto?.url) URL.revokeObjectURL(pendingPhoto.url); }, [pendingPhoto]);

  const dirty = !loadState.loading && (comparable(form) !== comparable(initial) || !!pendingPhoto);
  const cancelTo = isEdit ? `/clients/${id}` : '/clients';
  useUnsavedGuard(dirty && !saving, { confirm, navigate, message: 'Your changes to this client have not been saved.' });

  const liveErrors = useMemo(() => validate(form, initial), [form, initial]);
  const errorFor = (k) => serverErrors[k] || ((touched[k] || submitted) ? liveErrors[k] : undefined);
  const touch = (k) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (aiFilled.has(k)) setAiFilled((s) => { const n = new Set(s); n.delete(k); return n; });
    if (serverErrors[k]) setServerErrors((s) => { const n = { ...s }; delete n[k]; return n; });
  };

  // ── Section index: highlight the section in view ───────────────────────────
  useEffect(() => {
    if (loadState.loading) return undefined;
    const observers = SECTIONS.map(({ id: sid }) => {
      const el = document.getElementById(`sec-${sid}`);
      if (!el || typeof IntersectionObserver === 'undefined') return null;
      const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setActiveSection(sid); }, { rootMargin: '-25% 0px -65% 0px' });
      obs.observe(el);
      return obs;
    }).filter(Boolean);
    return () => observers.forEach((o) => o.disconnect());
  }, [loadState.loading]);

  const jumpTo = (sid) => {
    setActiveSection(sid);
    const el = document.getElementById(`sec-${sid}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const focusFirst = (errs) => {
    const first = FIELD_ORDER.find((k) => errs[k]);
    if (!first) return;
    const el = document.getElementById(fid(first));
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => el.focus({ preventScroll: true }), 250); }
  };

  // ── AI: fill blanks from a document ───────────────────────────────────────
  const handleAiExtract = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setExtracting(true);
    setAiResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/clients/extract-from-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ex = data.extracted || {};
      const map = {
        firstName: ex.firstName, lastName: ex.lastName, dateOfBirth: ex.dateOfBirth,
        gender: ex.gender, sex: ex.sex,
        ndisNumber: ex.ndisNumber, agedCareRecipientId: ex.agedCareRecipientId,
        referenceNumber: ex.referenceNumber,
        clientType: ex.clientType,
        address: ex.address, suburb: ex.suburb, state: ex.state, postcode: ex.postcode,
        generalInformation: ex.generalInformation,
        nextReviewDate: ex.nextReviewDate,
      };
      // Only blanks are filled; nothing typed is overwritten.
      const filled = [];
      setForm((f) => {
        const next = { ...f };
        for (const [k, v] of Object.entries(map)) {
          if (v && (!f[k] || f[k] === '' || f[k] === 'ndis_managed')) { next[k] = v; filled.push(k); }
        }
        // The richer care details ride along with the form and are saved when the client is created.
        next._ai = {
          environmentalDetails: ex.environmentalDetails,
          psychologicalDetails: ex.psychologicalDetails,
          sensoryDetails: ex.sensoryDetails,
          risksAndIssues: ex.risksAndIssues,
          goals: ex.goals,
          contacts: ex.contacts,
        };
        return next;
      });
      setAiFilled(new Set(filled));
      const extras = [];
      const count = (o) => (o ? Object.values(o).filter(Boolean).length : 0);
      if (count(ex.environmentalDetails)) extras.push(plural(count(ex.environmentalDetails), 'home detail'));
      if (count(ex.psychologicalDetails)) extras.push(plural(count(ex.psychologicalDetails), 'psychological detail'));
      if (count(ex.sensoryDetails)) extras.push(plural(count(ex.sensoryDetails), 'sensory detail'));
      if (ex.risksAndIssues?.length) extras.push(plural(ex.risksAndIssues.length, 'risk'));
      if (ex.goals?.length) extras.push(plural(ex.goals.length, 'goal'));
      if (ex.contacts?.length) extras.push(plural(ex.contacts.length, 'contact'));
      setAiExtras(extras);
      setAiResult({ ...ex, filled });
      toast.success(filled.length ? `Filled ${plural(filled.length, 'field')} from the document` : 'No blank fields could be filled from that document');
    } catch (err) {
      toast.error(err.response?.data?.message || 'We couldn’t read that document');
    } finally {
      setExtracting(false);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setFormError(null);
    const errs = validate(form, initial);
    if (Object.keys(errs).length) { focusFirst(errs); return; }

    if (isEdit && (form.defaultPriceBookId || '') !== (initial.defaultPriceBookId || '')) {
      const nextName = (priceBooks.find((p) => p.id === form.defaultPriceBookId) || {}).name;
      const ok = await confirm(
        `Changing the price book reprices future shifts. ${fullName(record) || 'This client'} moves to ${nextName || 'no price book'}.`,
        { title: form.defaultPriceBookId ? `Use ${nextName || 'this price book'}?` : 'Remove the price book?', confirmLabel: 'Save and change price book' },
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const { _ai, ...body } = form;
        await api.put(`/clients/${id}`, body);
        setInitial(form);
        toast.success('Client updated');
        navigate(`/clients/${id}`);
      } else {
        const { _ai, ...basicForm } = form;
        const { data } = await api.post('/clients', { ...basicForm, ...(_ai || {}) });
        if (pendingPhoto) {
          try {
            const fd = new FormData();
            fd.append('photo', new File([pendingPhoto.blob], 'photo.jpg', { type: 'image/jpeg' }));
            await api.post(`/clients/${data.id}/photo`, fd);
          } catch { toast.error('The client was created, but the photo didn’t upload. Add it from Edit.'); }
        }
        const extra = [];
        if (data.createdGoals?.length) extra.push(plural(data.createdGoals.length, 'goal'));
        if (data.createdContacts?.length) extra.push(plural(data.createdContacts.length, 'contact'));
        setInitial(form);
        setPendingPhoto(null);
        toast.success(`Client created${extra.length ? `, with ${extra.join(' and ')}` : ''}`, { duration: 4000 });
        navigate(`/clients/${data.id}`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'We couldn’t save. Check your connection, then try again.';
      const onField = fieldFromServer(msg);
      if (onField) { setServerErrors(onField); focusFirst(onField); }
      else setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const sectionDone = (s) => s.required && s.required.every((k) => String(form[k] || '').trim() && !liveErrors[k]);
  const sectionHasError = (sid) => Object.keys(SECTION_OF).some((k) => SECTION_OF[k] === sid && errorFor(k));
  const label = (text, key) => <>{text}{aiFilled.has(key) && <AiTag />}</>;

  // ── Render ────────────────────────────────────────────────────────────────
  if (isEdit && loadState.error) {
    return (
      <div className={PAGE}>
        <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message={loadState.error} onRetry={load} /></div>
      </div>
    );
  }
  if (loadState.loading) {
    return (
      <div className={PAGE} aria-busy="true" aria-label="Loading client">
        <div className="skeleton h-7 w-64" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <div className="skeleton h-4 w-40" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><div className="skeleton h-10" /><div className="skeleton h-10" /></div>
          </div>
        ))}
      </div>
    );
  }

  const inp = (k, extra = {}) => ({
    id: fid(k), value: form[k] ?? '', onChange: (e) => set(k, e.target.value), onBlur: () => touch(k),
    'aria-invalid': errorFor(k) ? true : undefined, className: clsx('input-field', extra.className), ...extra.props,
  });

  return (
    <div className={PAGE}>
      {isEdit && (
        <PageHeader
          before={(
            <Link to={`/clients/${id}`} className="mb-1 inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800">
              <ChevronLeft size={15} aria-hidden /> Back to {fullName(record) || 'client'}
            </Link>
          )}
          title={fullName(record) || 'Edit client'}
          description="Edit this participant’s details. Changes apply once you save."
        />
      )}

      <div className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-8 items-start">
        {/* Section index (wide screens) */}
        <nav aria-label="Form sections" className="hidden lg:block lg:sticky lg:top-24">
          <ul className="space-y-0.5">
            {SECTIONS.map((s) => {
              const on = activeSection === s.id;
              const err = sectionHasError(s.id);
              return (
                <li key={s.id}>
                  <a href={`#sec-${s.id}`} onClick={(e) => { e.preventDefault(); jumpTo(s.id); }}
                    aria-current={on ? 'true' : undefined}
                    className={clsx('flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors duration-150',
                      on ? 'bg-white text-slate-900 border border-slate-200' : 'text-slate-600 hover:text-slate-900 hover:bg-white/60 border border-transparent')}>
                    <span className="flex-1 truncate">{s.label}</span>
                    {err ? <span className="w-1.5 h-1.5 rounded-full bg-rose-500" aria-label="Has a problem" />
                      : sectionDone(s) ? <Check size={13} className="text-emerald-600" aria-label="Required fields filled" /> : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <form onSubmit={handleSubmit} noValidate className="min-w-0 space-y-5">
          {/* AI helper: fill the blanks from a plan or referral (new client only) */}
          {!isEdit && (
            <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <Sparkles size={18} className="text-violet-500 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">Have an NDIS plan or referral?</p>
                <p className="text-[13px] text-slate-500 mt-0.5">Upload it and we’ll fill the blanks. Anything you’ve already typed stays as it is.</p>
              </div>
              <input ref={aiFileRef} type="file" accept=".pdf,.docx,.doc,.txt,image/*" className="hidden" onChange={handleAiExtract} />
              <button type="button" onClick={() => aiFileRef.current?.click()} disabled={extracting} className="btn-secondary shrink-0">
                {extracting ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <FileUp size={15} aria-hidden />}
                {extracting ? 'Reading document…' : 'Choose document'}
              </button>
            </div>
          )}

          {aiResult && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" role="status">
              <div className="flex items-start gap-3">
                <Sparkles size={16} className="text-violet-500 mt-0.5 shrink-0" aria-hidden />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <p className="text-sm font-semibold text-slate-800">
                    {aiResult.filled.length ? `Filled ${plural(aiResult.filled.length, 'field')} from the document` : 'The document didn’t fill any blank fields'}
                  </p>
                  {aiResult.filled.length > 0 && (
                    <p className="text-[13px] text-slate-600">
                      {aiResult.filled.map((k) => FIELD_LABELS[k] || k).join(', ')}. They’re marked <AiTag /> until you change them. Check them before you save.
                    </p>
                  )}
                  {aiExtras.length > 0 && <p className="text-[13px] text-slate-600">Also found: {aiExtras.join(', ')}. These are added when you create the client.</p>}
                  {aiResult.supportNeeds?.length > 0 && (
                    <p className="text-[13px] text-slate-600">Support needs mentioned: {aiResult.supportNeeds.join(', ')}. Add them from the Overview after saving.</p>
                  )}
                  {aiResult.summary && <p className="text-[13px] text-slate-500">Summary: {aiResult.summary}</p>}
                </div>
                <button type="button" onClick={() => setAiResult(null)} aria-label="Dismiss"
                  className="p-2 -m-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={16} aria-hidden /></button>
              </div>
            </div>
          )}

          <FormSection id="basic" title="Basic information">
            <PhotoPicker clientId={isEdit ? id : null} photo={photoUrl} firstName={form.firstName} lastName={form.lastName}
              pending={pendingPhoto} onPending={setPendingPhoto} onSaved={setPhotoUrl} />
            <FormRow cols={2}>
              <Field htmlFor={fid('firstName')} label={label('First name', 'firstName')} required error={errorFor('firstName')}>
                <input {...inp('firstName')} required autoComplete="off" />
              </Field>
              <Field htmlFor={fid('lastName')} label={label('Last name', 'lastName')} required error={errorFor('lastName')}>
                <input {...inp('lastName')} required autoComplete="off" />
              </Field>
            </FormRow>
            <FormRow cols={3}>
              <Field htmlFor={fid('dateOfBirth')} label={label('Date of birth', 'dateOfBirth')} required error={errorFor('dateOfBirth')}>
                <input type="date" {...inp('dateOfBirth')} required max={todayIso()} />
              </Field>
              <Field htmlFor={fid('gender')} label={label('Gender', 'gender')}>
                <GenderSelect id={fid('gender')} value={form.gender} onChange={(v) => set('gender', v)} />
              </Field>
              <Field htmlFor={fid('sex')} label={label('Sex', 'sex')}>
                <select {...inp('sex')}>
                  <option value="">Not stated</option>
                  <option>Male</option><option>Female</option><option>Intersex</option>
                </select>
              </Field>
            </FormRow>
            <FormRow cols={2}>
              <Field htmlFor={fid('joinedDate')} label="Joined" hint={isEdit ? 'Set when the client was created.' : undefined}>
                <input type="date" {...inp('joinedDate')} disabled={isEdit} />
              </Field>
              <Field htmlFor={fid('nextReviewDate')} label={label('Next review', 'nextReviewDate')}>
                <input type="date" {...inp('nextReviewDate')} />
              </Field>
            </FormRow>
          </FormSection>

          <FormSection id="ids" title="Identification">
            <FormRow cols={2}>
              <Field htmlFor={fid('ndisNumber')} label={label('NDIS number', 'ndisNumber')} error={errorFor('ndisNumber')} hint="9 digits, starting with 43.">
                <input {...inp('ndisNumber')} inputMode="numeric" maxLength={11} placeholder="430000000" className="input-field tabular-nums" />
              </Field>
              <Field htmlFor={fid('agedCareRecipientId')} label={label('Aged care recipient ID', 'agedCareRecipientId')}>
                <input {...inp('agedCareRecipientId')} />
              </Field>
              <Field htmlFor={fid('clientIdExternal')} label="Client ID (external)"><input {...inp('clientIdExternal')} /></Field>
              <Field htmlFor={fid('referenceNumber')} label={label('Reference number', 'referenceNumber')}><input {...inp('referenceNumber')} /></Field>
              <Field htmlFor={fid('poNumber')} label="PO number"><input {...inp('poNumber')} /></Field>
              <Field htmlFor={fid('customField')} label="Custom field"><input {...inp('customField')} /></Field>
            </FormRow>
          </FormSection>

          <FormSection id="billing" title="Billing and settings">
            <FormRow cols={2}>
              <Field htmlFor={fid('clientType')} label={label('Plan management', 'clientType')}>
                <ClientTypeSelect id={fid('clientType')} value={form.clientType} onChange={(v) => set('clientType', v)} />
              </Field>
              <Field htmlFor={fid('defaultPriceBookId')} label="Default price book" hint={isEdit ? 'Changing it reprices future shifts. You’ll be asked to confirm.' : undefined}>
                <select {...inp('defaultPriceBookId')}>
                  <option value="">None assigned</option>
                  {priceBooks.map((pb) => <option key={pb.id} value={pb.id}>{pb.name}</option>)}
                </select>
              </Field>
              <Field htmlFor={fid('planPeriodStart')} label="NDIS plan start"><input type="date" {...inp('planPeriodStart')} /></Field>
              <Field htmlFor={fid('planPeriodEnd')} label="NDIS plan end" error={errorFor('planPeriodEnd')}><input type="date" {...inp('planPeriodEnd')} /></Field>
              <Field htmlFor={fid('invoiceDeliveryMethod')} label="Invoice delivery">
                <select {...inp('invoiceDeliveryMethod')}>
                  {INVOICE_DELIVERY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
            </FormRow>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 px-3.5 py-3">
              <div>
                <p className="text-sm font-medium text-slate-700">Share progress notes in the client portal</p>
                <p className="text-[12.5px] text-slate-500">Family members with portal access can read this client’s progress notes.</p>
              </div>
              <Switch checked={!!form.shareProgressNotes} onChange={(v) => set('shareProgressNotes', v)} label="Share progress notes in the client portal" />
            </div>
          </FormSection>

          <FormSection id="address" title="Address">
            <Field htmlFor={fid('address')} label={label('Street address', 'address')}
              hint={form.latitude != null ? (
                <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400" aria-hidden />Location pinned: the clock-in geofence uses this exact spot.</span>
              ) : undefined}>
              <AddressAutocomplete
                id={fid('address')}
                value={form.address}
                onChange={(v) => set('address', v)}
                onSelect={(a) => setForm((f) => ({ ...f, address: a.street || f.address, suburb: a.suburb || f.suburb, state: a.state || f.state, postcode: a.postcode || f.postcode, latitude: a.lat, longitude: a.lon }))}
                placeholder="Start typing an address…"
              />
            </Field>
            <FormRow cols={3}>
              <Field htmlFor={fid('suburb')} label={label('Suburb', 'suburb')}><input {...inp('suburb')} /></Field>
              <Field htmlFor={fid('state')} label={label('State', 'state')}>
                <select {...inp('state')}>
                  <option value="">Select…</option>
                  {STATES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field htmlFor={fid('postcode')} label={label('Postcode', 'postcode')} error={errorFor('postcode')}>
                <input {...inp('postcode')} inputMode="numeric" maxLength={4} className="input-field tabular-nums" />
              </Field>
            </FormRow>
          </FormSection>

          <FormSection id="safety" title="Safety and medical">
            <Field htmlFor={fid('riskAlert')}
              label={<span className="inline-flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" aria-hidden />Risk alert (shown to workers on the shift)</span>}>
              <textarea {...inp('riskAlert')} className="input-field min-h-[70px] resize-y"
                placeholder="Critical safety information workers must see before or at the shift (for example: aggression triggers, falls risk, no lone visits)…" />
            </Field>
            <Field htmlFor={fid('riskNotification')} label="Additional risk note">
              <textarea {...inp('riskNotification')} className="input-field min-h-[60px] resize-y" placeholder="A secondary risk note or handling instructions…" />
            </Field>
            <Field htmlFor={fid('allergies')} label="Allergies">
              <input {...inp('allergies')} placeholder="For example: penicillin, peanuts, latex" />
            </Field>
          </FormSection>

          <FormSection id="culture" title="Culture and preferences">
            <FormRow cols={2}>
              <Field htmlFor={fid('countryOfBirth')} label="Country of birth"><input {...inp('countryOfBirth')} /></Field>
              <Field htmlFor={fid('religion')} label="Religion"><input {...inp('religion')} /></Field>
              <Field htmlFor={fid('indigenousStatus')} label="Indigenous status">
                <select {...inp('indigenousStatus')}>
                  <option value="">Not stated</option>
                  <option value="aboriginal">Aboriginal</option>
                  <option value="torres_strait">Torres Strait Islander</option>
                  <option value="both">Aboriginal and Torres Strait Islander</option>
                  <option value="neither">Neither</option>
                </select>
              </Field>
              <Field htmlFor={fid('preferredCarerGender')} label="Preferred carer gender">
                <select {...inp('preferredCarerGender')}>
                  <option value="">No preference</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </Field>
              <Field htmlFor={fid('caldBackground')} label="CALD background"><input {...inp('caldBackground')} placeholder="Cultural or linguistic background" /></Field>
              <Field htmlFor={fid('languagesSpoken')} label="Languages spoken"><input {...inp('languagesSpoken')} placeholder="For example: English, Mandarin" /></Field>
            </FormRow>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 px-3.5 py-3">
              <p className="text-sm font-medium text-slate-700">Interpreter required</p>
              <Switch checked={!!form.interpreterRequired} onChange={(v) => set('interpreterRequired', v)} label="Interpreter required" />
            </div>
          </FormSection>

          <FormSection id="general" title="General information" hint="Visible to carers.">
            <Field htmlFor={fid('generalInformation')} label={label('General information', 'generalInformation')}>
              <textarea {...inp('generalInformation')} className="input-field min-h-[100px] resize-y" placeholder="Preferences, routines, communication style, pets…" />
            </Field>
          </FormSection>

          <FormSection id="private" title="Private notes" hint="Admin only. Carers don’t see these.">
            <Field htmlFor={fid('privateNotes')} label="Private notes">
              <textarea {...inp('privateNotes')} className="input-field min-h-[80px] resize-y" placeholder="Internal notes…" />
            </Field>
          </FormSection>

          {/* Save bar: stays at the bottom of the screen while the form scrolls */}
          <div className="sticky bottom-[calc(56px_+_env(safe-area-inset-bottom))] md:bottom-0 z-20 pb-3 pt-1">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card-deep">
              <div className="min-w-0 flex-1 text-[13px]">
                {formError ? (
                  <p role="alert" className="text-rose-700">{formError}</p>
                ) : submitted && Object.keys(liveErrors).length ? (
                  <p role="alert" className="text-rose-700">Fix {plural(Object.keys(liveErrors).length, 'field')} marked in red.</p>
                ) : dirty ? (
                  <p className="flex items-center gap-2 text-slate-700"><span className="w-2 h-2 rounded-full bg-amber-500" aria-hidden />Unsaved changes</p>
                ) : (
                  <p className="text-slate-500">{isEdit ? 'No changes yet' : 'Fields marked * are required'}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Link to={cancelTo} className="btn-secondary">Cancel</Link>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving && <Loader2 size={15} className="animate-spin" aria-hidden />}
                  {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create client'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
